import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Dialog from "@mui/material/Dialog";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import RaisedButton from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { withStyles } from "@mui/styles";
import TextField from "components/EnhancedTextField";
import { respondToCashboxApproval } from "services/cashboxApprovalService";
import { COMMENT_MAX_LENGTH } from "./constants";
import { styles } from "./styles";

/**
 * "Are you sure" for both answers. One comment box, two rules: optional under
 * Approve, required under Reject — the API enforces the same, this only spares
 * the customer a round trip.
 */
class ConfirmResponse extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      comment: "",
      commentErrorText: "",
      isLoading: false
    };
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleChange = event => {
    this.setState({ comment: event.target.value, commentErrorText: "" });
  };

  handleSubmit = () => {
    const { is_approve, order_id } = this.props;
    const comment = this.state.comment.trim();

    if (!is_approve && !comment) {
      this.setState({ commentErrorText: "A comment is required to reject" });
      return;
    }

    this.setState({ isLoading: true });

    this.props
      .dispatch(respondToCashboxApproval(order_id, is_approve, comment))
      .then(data => {
        if (this._isMounted) {
          this.setState({ isLoading: false });
          this.props.handleAnswered(data.cashbox_approval);
        }
      })
      .catch(() => {
        if (this._isMounted) {
          this.setState({ isLoading: false });
          // The error is already on the snackbar; the page reloads the card
          // because a refusal usually means the request moved underneath us.
          this.props.handleFailed();
        }
      });
  };

  render() {
    const { classes, is_approve, amounts } = this.props;
    const { comment, commentErrorText, isLoading } = this.state;

    const actions = isLoading ? (
      <CircularProgress />
    ) : (
      [
        <RaisedButton
          key="1"
          variant="text"
          color="primary"
          onClick={this.props.handleClose}
        >
          Cancel
        </RaisedButton>,
        <RaisedButton
          key="2"
          variant="contained"
          onClick={this.handleSubmit}
          className={is_approve ? classes.approve_button : classes.reject_button}
        >
          {is_approve ? "Confirm" : "Reject"}
        </RaisedButton>
      ]
    );

    return (
      <Dialog
        open={true}
        onClose={this.props.handleClose}
        sx={{
          "& .MuiDialog-paper": {
            minWidth: { xs: "auto", sm: "400px" }
          }
        }}
      >
        <DialogTitle>{is_approve ? "Confirm Payout" : "Reject Payout"}</DialogTitle>
        <DialogContent sx={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
          Are you sure you want to {is_approve ? "confirm" : "reject"} the payout of{" "}
          <strong>
            {amounts
              .map(
                amount =>
                  `${Number(amount.amount).toLocaleString()} ${amount.currency}`
              )
              .join(", ")}
          </strong>
          ?
          <TextField
            name="comment"
            floatingLabelText={is_approve ? "Comment" : "Comment *"}
            value={comment}
            onChange={this.handleChange}
            errorText={commentErrorText}
            maxLength={COMMENT_MAX_LENGTH}
            multiLine={true}
            fullWidth
          />
        </DialogContent>
        <DialogActions>{actions}</DialogActions>
      </Dialog>
    );
  }
}

ConfirmResponse.propTypes = {
  classes: PropTypes.object.isRequired,
  order_id: PropTypes.number.isRequired,
  is_approve: PropTypes.bool.isRequired,
  amounts: PropTypes.array.isRequired,
  handleAnswered: PropTypes.func.isRequired,
  handleFailed: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  return {};
};

export default withStyles(styles)(connect(mapStateToProps)(ConfirmResponse));
